import React from "react";
import Button, { ButtonProps } from "@material-ui/core/Button";
import styled from "styled-components";

import { color, fontSize } from "style/constants";

interface StyledButton {
  background?: string;
  backgroundImage?: string;
  borderRadius?: string;
  compressed?: boolean;
  disabled?: boolean;
  ellipsis?: boolean;
  fontSize?: fontSize;
  icon?: boolean;
  inverted?: boolean;
  secondary?: boolean;
  jumbo?: boolean;
  longText?: boolean;
  margin?: string;
  plain?: boolean;
  plainBlue?: boolean;
  padding?: string;
  squared?: boolean;
  stickyFooter?: boolean;
  textColor?: string;
  warning?: boolean;
  wide?: boolean;
  width?: string;
}

// Extending our custom type with material props
export type StyledButtonProps = StyledButton & ButtonProps;

const backgroundImageStyle = `
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
`;

const compressedStyle = `
  height: 32px;
  line-height: 0;
`;

const disabledStyle = `
  background-color: ${color.GRAY};
  color: ${color.BLACK};
  border-color: ${color.GRAY};
`;

const iconStyle = `
&& {
  border-radius: 50px;
  height: 50px;
  min-width: unset;
  padding: 0;
  span > * {
    margin-left: 1rem;
  }
}
`;

const invertedStyle = `
  background-color: ${color.WHITE};
  border: 1px solid ${color.BLUE};
  color: ${color.DARKBLUE};
`;

const warningStyle = `
  background-color: ${color.RED};
  color: ${color.WHITE};
`;

const jumboStyle = `
  border-radius: 0px;
  padding: 20px 98px;
  height: 60px;
  width: 375px;
`;

const plainStyle = `
  background-color: unset;
  color: ${color.BLACK};
  height: 100%;
  padding: 0;
`;

const plainBlueStyle = `
  background-color: unset;
  color: ${color.BLUE};
  height: 100%;
  padding: 0;
`;

const stickyFooterStyle = `
  border-radius: 0px;
  height: 60px;
  position: fixed;
  bottom: 0;
  left: 0;
`;

const secondaryStyle = `
  border-color: ${color.LIGHTERGRAY};
  border: 1px solid ${color.LIGHTERGRAY};
  color: ${color.BLACK};
  background: ${color.WHITE}
`;

const ellipsisStyle = `
  .MuiButton-label{
    display: -webkit-box;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    line-height: 1.75;
  }
`;

// Destructuring props passed to styled Material UI component to avoid console warnings for props passed directly to underlying DOM element
const StyledButton = styled(
  ({
    background,
    backgroundImage,
    borderRadius,
    color,
    compressed,
    ellipsis,
    fontSize,
    icon,
    inverted,
    jumbo,
    // removes left and right padding to accomodate long text
    longText,
    margin,
    plain,
    plainBlue,
    squared,
    stickyFooter,
    textColor,
    warning,
    wide,
    width,
    secondary,
    ...otherProps
  }) => <Button {...otherProps} />
)`
  && {
    ${props => props.backgroundImage && backgroundImageStyle}
    ${props => props.compressed && compressedStyle}
    ${props => props.icon && iconStyle}
    ${props => props.inverted && invertedStyle}
    ${props => props.jumbo && jumboStyle}
    ${props => props.plain && plainStyle}
    ${props => props.plainBlue && plainBlueStyle}
    ${props => props.stickyFooter && stickyFooterStyle}
    ${props => props.secondary && secondaryStyle}
    ${props => props.disabled && disabledStyle}
    ${props => props.ellipsis && ellipsisStyle}
    ${props => props.warning && warningStyle}
    ${props => props.backgroundImage && 'background-image: url("' + props.backgroundImage + '");'}
    ${props => props.padding && "padding:" + props.padding + ";"}
    background-color: ${props => props.background};
    border-radius: ${props => props.squared && "5px"};
    border-radius: ${props => props.borderRadius};
    color: ${props => props.textColor};
    font-size: ${props => props.fontSize};
    padding: ${props => props.wide && "14px 88px"};
    padding: ${props => props.longText && "14px 0"};
    margin: ${props => props.margin};
    width: ${props => props.width};
    text-transform: none;
  }
` as React.FC<StyledButtonProps>;

export default StyledButton;
